import React, { useEffect, useState } from "react";
import { PageShell, PageHero, PageSection } from "../components/PageShell";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import AdminNav from "../components/AdminNav";
import { adminService } from "../services/adminService";
import FormModal from "../components/FormModal";
import { toast } from "sonner";

const ICONS = ["Wrench", "Sparkles", "Clock", "Rocket"];

export default function AdminFutureAdminPage() {
  const [items, setItems] = useState([]);
  const [nearTermFocus, setNearTermFocus] = useState("");
  const [error, setError] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ icon: "Wrench", title: "", desc: "", order: 0, isPublished: true });

  async function load() {
    setError("");
    const res = await adminService.getFutureItemsAdmin();
    if (!res.ok) return setError(res.error || "Failed to load future items");
    setItems(res.data?.items || []);
    setNearTermFocus(res.data?.nearTermFocus || "");
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageShell>
      <PageHero eyebrow="Admin" title="Future Updates" highlight="content." description="Manage roadmap items and near-term focus text." />
      <PageSection variant="content">
        <div className="mx-auto max-w-7xl grid gap-6">
          <AdminNav />
          {error ? <Card><CardContent className="py-4 text-sm text-destructive">{error}</CardContent></Card> : null}

          <Card className="shadow-soft">
            <CardHeader><CardTitle>Near-term focus</CardTitle></CardHeader>
            <CardContent className="grid gap-3">
              <textarea className="min-h-28 px-3 py-2 rounded-xl border border-border bg-background text-sm" value={nearTermFocus} onChange={(e) => setNearTermFocus(e.target.value)} />
              <div className="flex gap-2">
                <Button size="sm" onClick={async () => {
                  const r = await adminService.updateFutureMeta({ nearTermFocus });
                  if (!r.ok) return toast.error(r.error || "Update failed");
                  toast.success("Near-term focus updated");
                  await load();
                }}>Save focus</Button>
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div><Button onClick={() => { setDraft({ icon: "Wrench", title: "", desc: "", order: 0, isPublished: true }); setAddOpen(true); }}>Add roadmap item</Button></div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {items.map((it) => (
              <Card key={it._id} className="shadow-soft">
                <CardHeader><CardTitle>{it.title}</CardTitle></CardHeader>
                <CardContent className="text-sm text-muted-foreground space-y-3">
                  <div className="text-xs">icon: {it.icon}</div>
                  <div>{it.desc}</div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => { setEditing(it); setDraft({ icon: it.icon, title: it.title, desc: it.desc, order: it.order || 0, isPublished: it.isPublished !== false }); setEditOpen(true); }}>Edit</Button>
                    <Button size="sm" variant="outline" onClick={async () => { const r = await adminService.deleteFutureItem(it._id); if (!r.ok) return toast.error(r.error || "Delete failed"); toast.success("Item deleted"); await load(); }}>Delete</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </PageSection>

      <FormModal open={addOpen} title="Add Roadmap Item" onClose={() => setAddOpen(false)}>
        <div className="grid gap-3">
          <select className="h-10 px-3 rounded-xl border border-border bg-background text-sm" value={draft.icon} onChange={(e) => setDraft((p) => ({ ...p, icon: e.target.value }))}>
            {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Title" value={draft.title} onChange={(e) => setDraft((p) => ({ ...p, title: e.target.value }))} />
          <textarea className="min-h-28 px-3 py-2 rounded-xl border border-border bg-background text-sm" placeholder="Description" value={draft.desc} onChange={(e) => setDraft((p) => ({ ...p, desc: e.target.value }))} />
          <div className="flex gap-2">
            <Button size="sm" onClick={async () => {
              const r = await adminService.createFutureItem(draft);
              if (!r.ok) return toast.error(r.error || "Create failed");
              toast.success("Item created");
              setAddOpen(false);
              await load();
            }}>Save</Button>
            <Button size="sm" variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
          </div>
        </div>
      </FormModal>

      <FormModal open={editOpen} title="Edit Roadmap Item" onClose={() => setEditOpen(false)}>
        <div className="grid gap-3">
          <select className="h-10 px-3 rounded-xl border border-border bg-background text-sm" value={draft.icon} onChange={(e) => setDraft((p) => ({ ...p, icon: e.target.value }))}>
            {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
          <input className="h-10 px-3 rounded-xl border border-border bg-background text-sm" placeholder="Title" value={draft.title} onChange={(e) => setDraft((p) => ({ ...p, title: e.target.value }))} />
          <textarea className="min-h-28 px-3 py-2 rounded-xl border border-border bg-background text-sm" placeholder="Description" value={draft.desc} onChange={(e) => setDraft((p) => ({ ...p, desc: e.target.value }))} />
          <div className="flex gap-2">
            <Button size="sm" onClick={async () => {
              if (!editing?._id) return;
              const r = await adminService.updateFutureItem(editing._id, draft);
              if (!r.ok) return toast.error(r.error || "Update failed");
              toast.success("Item updated");
              setEditOpen(false);
              await load();
            }}>Update</Button>
            <Button size="sm" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
          </div>
        </div>
      </FormModal>
    </PageShell>
  );
}

